import {
  KitchenSinkResult,
  CreateRoomResult,
  RespondRequest,
  PlayerTeamRequest,
  UpdateTeamsRequest,
  ActivateMoveRequest,
  UpdateMoveRequest,
  SubmitMoveRequest,
  PlayAgainRequest,
  PlayAgainResult,
  KickRequest,
  SelectDrawingRequest,
  SetMovePhaseRequest,
  VoteDrawingRequest,
  CreateRoomRequest,
  SetPromptFormatsRequest,
  PlaceSideBetRequest,
  GalleryResult
} from '@shared/api-dto';
import { PromptFormat } from '@shared/constants';

const BASE_URL = '/api/';
const UPLOAD_URL = '/upload';
const BASE_OPTIONS: RequestInit = {
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json'
  }
};

async function get (stub: string) {
  const response = await fetch(BASE_URL + stub, BASE_OPTIONS);
  const result = await response.json();
  if (response.ok) {
    return result;
  }

  throw result.error || 'unknown error in ' + stub;
}

async function post (stub: string, data: Record<string, any>) {
  const response = await fetch(BASE_URL + stub, {
    ...BASE_OPTIONS,
    method: 'POST',
    body: JSON.stringify(data)
  });
  const result = await response.json();
  if (response.ok) {
    return result;
  }

  throw result.error || 'unknown error when POST to ' + stub;
}

export async function getKitchenSink (): Promise<KitchenSinkResult> {
  return get('kitchensink');
}

export async function getGallery (): Promise<GalleryResult> {
  return get('gallery');
}

export async function createRoom (promptFormats?: PromptFormat[]): Promise<CreateRoomResult> {
  const payload: CreateRoomRequest = {};
  if (promptFormats) {
    payload.promptFormats = promptFormats;
  }
  return post('createroom', payload);
}

export async function playAgain (payload: PlayAgainRequest): Promise<PlayAgainResult> {
  return post('playagain', payload);
}

export async function setPromptFormats (payload: SetPromptFormatsRequest): Promise<void> {
  return post('setpromptformats', payload);
}

export async function respondToPrompt (payload: RespondRequest): Promise<void> {
  return post('respond', payload);
}

export async function updateName (code: string, name: string): Promise<void> {
  return post('updatename', {
    name,
    code
  });
}

export async function updateTeams (
  code: string,
  updates: PlayerTeamRequest[],
  promptFormats?: PromptFormat[]
): Promise<void> {
  const payload: UpdateTeamsRequest = {
    code,
    updates,
    promptFormats
  };
  return post('updateteams', payload);
}

export async function activateMove (payload: ActivateMoveRequest) {
  return post('activatemove', payload);
}

export async function setMove (payload: UpdateMoveRequest) {
  return post('setmove', payload);
}

export async function submitMove (payload: SubmitMoveRequest) {
  return post('submitmove', payload);
}

export async function endMove (payload: SubmitMoveRequest) {
  return post('endmove', payload);
}

export async function kickPlayer (payload: KickRequest) {
  return post('kickplayer', payload);
}

export async function selectDrawing (payload: SelectDrawingRequest) {
  return post('selectdrawing', payload);
}

export async function setMovePhase (payload: SetMovePhaseRequest) {
  return post('setmovephase', payload);
}

export async function voteDrawing (payload: VoteDrawingRequest) {
  return post('votedrawing', payload);
}

export async function placeSideBet (payload: PlaceSideBetRequest) {
  return post('placesidebet', payload);
}

// Function to upload drawing as form data
export async function uploadDrawing (
  assignmentData: {
    roomCode: string;
    userGuid: string;
  },
  drawingDataUrl: string
): Promise<void> {
  // Convert data URL to Blob
  const response = await fetch(drawingDataUrl);
  const blob = await response.blob();

  // Create FormData and append file and assignment data
  const formData = new FormData();
  formData.append('drawing', blob, 'drawing.png');
  formData.append('roomCode', assignmentData.roomCode);
  formData.append('userGuid', assignmentData.userGuid);

  // Upload using fetch with FormData
  const uploadResponse = await fetch(UPLOAD_URL, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData
  });

  const result = await uploadResponse.json();
  if (uploadResponse.ok) {
    return result;
  }

  throw result.error || 'unknown error when uploading drawing';
}
